import * as React from 'react'
import styled from 'styled-components'
import { tJa } from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { dmcTourService, postService, utilService } from '../../../../core/services'
import { IDmcTourParams } from '../../../../core/services/DmcTourService'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import injectGoogleMaps from '../../../../utils/injectGoogleMaps'
import { Button } from '../../../atoms'
import SvgIcon from '../../../atoms/SvgIcon'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { DmcSteps, Footer, Main, Wrapper } from '../../../organisms/DmcTours/Frame'
import { TourForm, TOUR_SECTIONS, IDmcTourFormValues } from '../../../organisms/DmcTours/TourForm'

declare var window: interfaces.IWindow

interface IProps {
  google: any
  isNew: boolean
  user: any
  areas: IJsonResponse
  languages: IJsonResponse
  agent_ranks: IJsonResponse
  tour_skills: IJsonResponse
  // 編集時のみ
  dmc_tour_id?: number
  exclusive?: boolean
  post?: IJsonResponse
  special_ranks?: IJsonResponse
  sound_out_templates?: IJsonResponse
  formal_request_templates?: IJsonResponse
}

const buildInitialTour = (post: any, exclusive?: boolean): IDmcTourFormValues => ({
  short_name: post?.short_name ?? '',
  post_type: post?.post_type ?? 'private',
  exclusive: exclusive ?? false,
  area_id: post?.area_id ? String(post.area_id) : '',
  tour_group_max_attendees: post?.tour_group_max_attendees
    ? String(post.tour_group_max_attendees)
    : '',
  language_ids: (post?.languages || []).map(language => language.id),
  title: post?.name ?? '',
  title_same_as_short_name: post ? post.name === post.short_name : true,
  guide_meeting_time_offset:
    post?.guide_meeting_time_offset == null ? '' : String(post.guide_meeting_time_offset),
  post_cost: {
    default_travel_cost: post?.post_cost?.default_travel_cost ?? constants.DEFAULT_TRAVEL_COST,
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].reduce(
      (acc, num) => ({
        ...acc,
        [`special_activity_fee_${num}`]: post?.post_cost?.[`special_activity_fee_${num}`] ?? 0,
      }),
      {}
    ),
    special_activity_fee_over_base: post?.post_cost?.special_activity_fee_over_base ?? 0,
    special_activity_fee_over_unit: post?.post_cost?.special_activity_fee_over_unit ?? 0,
    large_group_min_people: post?.post_cost?.large_group_min_people ?? 0,
    large_group_fee: post?.post_cost?.large_group_fee ?? 0,
  },
  post_rank_fees: (post?.post_rank_fees || []).map(fee => ({
    agent_rank_id: fee.agent_rank_id,
    amount: String(fee.amount ?? ''),
  })),
  meeting_points: (post?.meeting_points || []).map(point => ({
    name: point.name ?? '',
    location: point.location ?? '',
    latitude: point.latitude,
    longitude: point.longitude,
    memo: point.memo ?? '',
  })),
  post_tour_skill: {
    tour_skill_id: post?.post_tour_skill?.tour_skill_id
      ? String(post.post_tour_skill.tour_skill_id)
      : '',
    level: post?.post_tour_skill?.level == null ? '' : String(post.post_tour_skill.level),
  },
})

const DmcTourNew: React.FC<IProps> = props => {
  // user はビューから属性ハッシュで渡される(pages/agent/posts/new と同じ)
  const user = props.user
  const { data: areas } = utilService.getDataFromJson(props.areas)
  const { data: languages } = utilService.getDataFromJson(props.languages)
  const { data: agentRanks } = utilService.getDataFromJson(props.agent_ranks)
  const { data: tourSkills } = utilService.getDataFromJson(props.tour_skills)
  const { data: specialRanks } = utilService.getDataFromJson(props.special_ranks || { data: [] })
  const { data: soundOutTemplates } = utilService.getDataFromJson(
    props.sound_out_templates || { data: [] }
  )
  const { data: formalRequestTemplates } = utilService.getDataFromJson(
    props.formal_request_templates || { data: [] }
  )
  const post = props.post ? postService.getPostFromJson(props.post).post : null

  Hooks.SetDocumentTitle(tJa(props.isNew ? 'dmc_tour.title_new' : 'dmc_tour.title_edit'))

  const [activeSection, setActiveSection] = React.useState(TOUR_SECTIONS[0].id)
  const [tour, setTour] = React.useState<IDmcTourFormValues>(() =>
    buildInitialTour(post, props.exclusive)
  )
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const validate = () => {
    if (!tour.short_name.trim() || !tour.area_id || !tour.title.trim()) {
      window.flashMessages.addMessage({
        text: tJa('dmc_tour.required_tour_fields'),
        type: 'error',
      })
      return false
    }
    // 通常ランクのガイド料金は全て必須(サーバ側の missing_payment_settings と同じ条件)
    const missingGuideFee = agentRanks
      .filter(rank => rank.rank_kind === 'normal')
      .some(rank => {
        const amount = tour.post_rank_fees.find(fee => fee.agent_rank_id === rank.id)?.amount
        return amount == null || String(amount).trim() === ''
      })
    if (missingGuideFee) {
      window.flashMessages.addMessage({
        text: tJa('dmc_tour.guide_fee_required'),
        type: 'error',
      })
      return false
    }
    return true
  }

  // 新規作成後はゲスト情報の入力へ進み、編集時はこの画面に留まる(遷移先はサーバの応答で決まる)
  const handleSubmit = async () => {
    if (!validate()) {
      return
    }
    setIsSubmitting(true)
    try {
      const { title_same_as_short_name, ...tourParams } = tour
      const params = { tour: tourParams as IDmcTourParams }
      const data = props.isNew
        ? await dmcTourService.create(params)
        : await dmcTourService.update(props.dmc_tour_id, params)
      window.flashMessages.addMessage({ text: data.flush.message, type: data.flush.type })
      if (data.redirect_path) {
        window.location.href = data.redirect_path
      } else {
        setIsSubmitting(false)
      }
    } catch (error) {
      // サーバエラーのメッセージ表示は httpClient のインターセプタが行うため、ここでは送信状態を戻すだけ
      setIsSubmitting(false)
    }
  }

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        {/* 通常ツアー作成画面(pages/agent/posts/new)のNavと同じ見た目のヘッダー */}
        <PageTitle>{tJa(props.isNew ? 'dmc_tour.title_new' : 'dmc_tour.title_edit')}</PageTitle>
        <Nav>
          <ul className="Nav_List">
            {TOUR_SECTIONS.map(section => (
              <li key={section.id} className={section.id === activeSection ? 'active' : undefined}>
                <a href={`#${section.id}`} onClick={() => setActiveSection(section.id)}>
                  {tJa(section.label)}
                </a>
              </li>
            ))}
          </ul>
        </Nav>
        <Main>
          <DmcSteps current={0} />
          <TourForm
            google={props.google}
            isNew={props.isNew}
            values={tour}
            onChange={setTour}
            areas={areas}
            languages={languages}
            agentRanks={agentRanks}
            tourSkills={tourSkills}
            ownerPostId={post?.id}
            specialRanks={specialRanks}
            soundOutTemplates={soundOutTemplates}
            formalRequestTemplates={formalRequestTemplates}
          />
          <Footer className="end">
            <Button
              textColor={constants.COLORS.white}
              backgroundColor={constants.COLORS.primary}
              disabled={isSubmitting}
              handleClick={handleSubmit}
            >
              {tJa(
                isSubmitting
                  ? 'generic.saving'
                  : props.isNew
                  ? 'dmc_tour.save_and_go_to_guests'
                  : 'generic.save'
              )}
            </Button>
            {!props.isNew && (
              <Button>
                <a href={`/agent/dmc_tours/${props.dmc_tour_id}/guests`}>
                  {tJa('dmc_tour.go_to_guests')}
                  <SvgIcon name="arrow_forward" width={16} height={16} className="icon" />
                </a>
              </Button>
            )}
          </Footer>
        </Main>
      </Wrapper>
    </SidebarLayout>
  )
}

const PageTitle = styled.h1`
  margin: 0 40px 16px;
  font-size: 22px;
  font-weight: bold;
  color: ${constants.COLORS.black};
`

// ヘッダーの配色・寸法はモック(tour/Main.dc.html)に合わせて直接指定
const Nav = styled.section`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 0 40px;
  background-color: ${constants.COLORS.background};
  user-select: none;

  .Nav_List {
    display: flex;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
    overflow-x: auto;
    white-space: nowrap;

    li {
      font-size: 13px;

      a {
        display: block;
        padding: 10px 16px;
        color: ${constants.COLORS.subText};
        border-bottom: 2px solid transparent;
      }

      &.active a {
        color: ${constants.COLORS.primary};
        font-weight: bold;
        border-bottom-color: ${constants.COLORS.primary};
      }

      &:hover:not(.active) a {
        color: ${constants.COLORS.primary};
      }
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px 0;
  }
`

export default injectGoogleMaps(DmcTourNew)
